import { Injectable } from '@angular/core';
import { ToastService } from '@msi/cobalt';
import { BehaviorSubject, Observable } from 'rxjs';
import { OrcUserInfo } from 'src/app/interfaces/orc-user-info.interface';
import { AgreementsModel, PrivacyModel } from 'src/app/interfaces/privacy-model.interface';
import { ApiService } from '../api/api.service';
import { LoadingErrorsService } from '../loading-errors/loading-errors.service';
import { share } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { notNull } from 'src/app/utils/rxjs-custom-operators/rxjs-custom-operators.utils';

@Injectable({
	providedIn: 'root',
})
export class AgreementsModalInvokerService {
	agreements: AgreementsModel[];
	userInfo: OrcUserInfo;
	public agreementsUpdatedSinceLastVisit = false;
	private _agreementsToBeAccepted: PrivacyModel[];

	private loadingDataSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
	public get loadingData$(): Observable<boolean> {
		return this.loadingDataSubject.asObservable();
	}

	private hasAcceptedAllAgreementsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
	public get hasAcceptedAllAgreements$(): Observable<boolean> {
		return this.hasAcceptedAllAgreementsSubject.asObservable().pipe(notNull());
	}

	constructor(
		private apiService: ApiService,
		private toastService: ToastService,
		private loadingErrorsService: LoadingErrorsService
	) {}

	/** Gets the user info with user accepted agreements, all agreements from Orchestrate
	 *  and processes the agreements in this.agreements and this._agreementsToBeAccepted */
	public loadAgreements() {
		// Loads only if previously user data was not loaded meaning if loading data is false
		if (!this.loadingDataSubject.value) return;

		this.apiService.getOrcUserInfo().subscribe({
			next: orcUserInfo => {
				this.userInfo = orcUserInfo;
				this.getAgreements();
			},
			error: (err: HttpErrorResponse) => {
				this.loadingErrorsService.openErrorPage(
					err?.status?.toString(),
					err?.statusText,
					$localize`Failed to load user information. If issues persist, contact your system administrator.`
				);
			},
		});
	}

	public getAgreements() {
		this.apiService.getAgreementsOrc().subscribe({
			next: agreementsFromGet => {
				this.agreements = agreementsFromGet;
				this._agreementsToBeAccepted = this.determineUserAgreementsToAccept(this.agreements);
				this.hasAcceptedAllAgreementsSubject.next(this._agreementsToBeAccepted.length === 0);
				this.loadingDataSubject.next(false);
			},
			error: () => {
				this.toastService.error($localize`Error loading user agreements.`);
			},
		});
	}

	public patchUserAgreementsOrc() {
		const req = this.apiService
			.patchUserAgreementsOrc(this.agreementsToBeAccepted.map(a => a.agreementId))
			.pipe(share());
		req.subscribe({
			next: () => {
				this._agreementsToBeAccepted = [];
				this.hasAcceptedAllAgreementsSubject.next(true);
			},
			error: () => {
				this.toastService.error($localize`Could not submit agreements. Please try again later.`, null, {
					autoDismiss: 10000,
				});
			},
		});
		return req;
	}

	public get agreementsToBeAccepted() {
		return this._agreementsToBeAccepted;
	}

	private determineUserAgreementsToAccept(agreements: AgreementsModel[]): PrivacyModel[] {
		if (this.userInfo && this.userInfo.acceptedAgreements?.length > 0) {
			agreements = agreements.filter(agreement => {
				const userInfoAgreement = this.userInfo.acceptedAgreements.find(aa => aa.agreementId === agreement.id);
				if (userInfoAgreement) {
					if (new Date(userInfoAgreement.acceptedAt) < new Date(agreement?.lastUpdated)) {
						this.agreementsUpdatedSinceLastVisit = true;
						return true;
					} else {
						return false;
					}
				} else {
					return true;
				}
			});
		}
		return agreements.map(agreement => {
			return {
				name: agreement.name,
				url: agreement.url,
				checked: agreement.checked,
				agreementId: agreement.id,
				acceptedAt:
					this.userInfo.acceptedAgreements.find(userInfoAgreement => userInfoAgreement.agreementId === agreement.id)
						?.acceptedAt ?? null,
			};
		});
	}
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
